import React, {useEffect, useState} from 'react';
import {Button, Card, CardBody, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row} from 'reactstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {connect} from 'react-redux';
import {IRootState} from 'app/shared/reducers';
import {IVehicleType} from 'app/shared/model/vehicleType.model';
import {getEntities as getVehicleTypes} from 'app/entities/vehicleType/vehicleType.reducer';
import {IJourney} from 'app/shared/model/journey.model';

interface VehicleTypeSelectionModalProps extends StateProps, DispatchProps {
  isOpen: boolean;
  toggle: () => void;
  onSave: (vehicleTypeId: string) => void;
  journey: IJourney;
}

const VehicleTypeSelectionModalComponent = (props: VehicleTypeSelectionModalProps) => {
  const {isOpen, toggle, onSave, journey, vehicleTypes, loading} = props;
  const [selectedVehicleType, setSelectedVehicleType] = useState<IVehicleType | null>(null);
  const [availableVehicleTypes, setAvailableVehicleTypes] = useState<IVehicleType[]>([]);

  const patientCountryId = journey?.patientCase?.patient?.countryId;
  const pickupLocation = journey.startHealthcareFacility || journey.startVillage;
  const dropOffLocation = journey.endHealthcareFacility || journey.endVillage;

  useEffect(() => {
    if (isOpen && (!vehicleTypes || vehicleTypes.length === 0)) {
      props.getVehicleTypes();
    }
  }, [isOpen]);

  useEffect(() => {
    if (vehicleTypes && vehicleTypes.length > 0 && patientCountryId) {
      // Filter vehicle types by country
      const filtered = vehicleTypes.filter(vt => vt.countryId === patientCountryId);
      setAvailableVehicleTypes(filtered);
    }
  }, [vehicleTypes, patientCountryId]);

  useEffect(() => {
    // Reset selected vehicle type when modal opens
    if (isOpen) {
      setSelectedVehicleType(journey.vehicleType || null);
    }
  }, [isOpen, journey.vehicleType]);

  const handleVehicleTypeSelect = (vehicleType: IVehicleType) => {
    setSelectedVehicleType(vehicleType);
  };

  const handleSave = () => {
    if (selectedVehicleType) {
      // Show confirmation dialog
      const confirm = window.confirm(`This action can not be undone. Are you sure you want to select ${selectedVehicleType.enType}?`);
      if (!confirm) {
        return;
      }
      onSave(selectedVehicleType.id);
      toggle();
    }
  };

  const getVehicleTypeImage = (vehicleType: IVehicleType) => {
    if (vehicleType.image) {
      return `data:image/svg+xml;base64,${vehicleType.image}`;
    }
    return null;
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" backdrop="static">
      <ModalHeader toggle={toggle}>
        Select Vehicle Type
      </ModalHeader>
      <ModalBody>
        <div className="mb-3">
          <p className="text-muted mb-1">
            <strong>From:</strong> {pickupLocation?.name || 'Not Selected'}
          </p>
          <p className="text-muted mb-0">
            <strong>To:</strong> {dropOffLocation?.name || 'Not Selected'}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <FontAwesomeIcon icon="spinner" spin size="2x"/>
            <p className="mt-2">Loading vehicle types...</p>
          </div>
        ) : availableVehicleTypes.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted">No vehicle types available for this location.</p>
          </div>
        ) : (
          <Row>
            {availableVehicleTypes.map((vehicleType) => {
              const isSelected = selectedVehicleType?.id === vehicleType.id;
              const hasImage = getVehicleTypeImage(vehicleType);

              return (
                <Col md="12" key={vehicleType.id} className="mb-3">
                  <Card
                    className={`vehicle-type-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleVehicleTypeSelect(vehicleType)}
                    style={{cursor: 'pointer'}}
                  >
                    <CardBody className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        {hasImage ? (
                          <img
                            src={getVehicleTypeImage(vehicleType)}
                            alt={vehicleType.enType}
                            className="vehicle-type-icon mr-3"
                            style={{width: '50px', height: '50px', objectFit: 'contain'}}
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon="truck"
                            size="2x"
                            className="mr-3 text-primary"
                          />
                        )}
                        <div>
                          <h5 className="mb-0">{vehicleType.enType}</h5>
                          {vehicleType.vehicleLevel && (
                            <small className="text-muted">Level {vehicleType.vehicleLevel}</small>
                          )}
                        </div>
                      </div>
                      <Button
                        color="primary"
                        disabled={isSelected}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVehicleTypeSelect(vehicleType);
                        }}
                      >
                        {isSelected ? 'SELECTED' : 'SELECT'}
                      </Button>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSave}
          disabled={!selectedVehicleType}
        >
          Save Vehicle Type
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const mapStateToProps = (storeState: IRootState) => ({
  vehicleTypes: storeState.vehicleType.entities,
  loading: storeState.vehicleType.loading,
});

const mapDispatchToProps = {
  getVehicleTypes,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

const VehicleTypeSelectionModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(VehicleTypeSelectionModalComponent);

export default VehicleTypeSelectionModal;
